import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Handle, Position, type Node, type NodeProps } from "@xyflow/react";
import {
  Bot,
  Brain,
  Coins,
  FileEdit,
  FileInput,
  FileStack,
  GitBranch,
  ListChecks,
  Play,
  SendHorizonal,
  ShieldCheck,
  Square,
  UserCog,
  Users,
} from "lucide-react";

import { cn } from "@/lib/utils";
import type { ProductNodeType } from "@/services/platformEnterprisesService";

export interface BuilderNodeCase {
  id: string;
  label: string;
}

export interface BuilderNodeData extends Record<string, unknown> {
  label?: string;
  description?: string;
  __nodeType: ProductNodeType;
  __hasError?: boolean;
  __branchCases?: BuilderNodeCase[];
  __decisionOptions?: BuilderNodeCase[];
}

export type BuilderNode = Node<BuilderNodeData, "builder">;

const NODE_STYLES: Record<
  ProductNodeType,
  { icon: typeof Play; color: string; bg: string; border: string; iconColor: string }
> = {
  start: {
    icon: Play,
    color: "text-emerald-700",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    iconColor: "text-emerald-600",
  },
  end: {
    icon: Square,
    color: "text-slate-700",
    bg: "bg-slate-50",
    border: "border-slate-300",
    iconColor: "text-slate-600",
  },
  precondition: {
    icon: ShieldCheck,
    color: "text-amber-700",
    bg: "bg-amber-50",
    border: "border-amber-200",
    iconColor: "text-amber-600",
  },
  userForm: {
    icon: FileEdit,
    color: "text-sky-700",
    bg: "bg-sky-50",
    border: "border-sky-200",
    iconColor: "text-sky-600",
  },
  staffApproval: {
    icon: UserCog,
    color: "text-violet-700",
    bg: "bg-violet-50",
    border: "border-violet-200",
    iconColor: "text-violet-600",
  },
  staffAction: {
    icon: Brain,
    color: "text-violet-700",
    bg: "bg-violet-50",
    border: "border-violet-200",
    iconColor: "text-violet-600",
  },
  branch: {
    icon: GitBranch,
    color: "text-rose-700",
    bg: "bg-rose-50",
    border: "border-rose-200",
    iconColor: "text-rose-600",
  },
  pricing: {
    icon: Coins,
    color: "text-emerald-700",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    iconColor: "text-emerald-600",
  },
  internalRequest: {
    icon: FileInput,
    color: "text-indigo-700",
    bg: "bg-indigo-50",
    border: "border-indigo-200",
    iconColor: "text-indigo-600",
  },
  inviteActors: {
    icon: Users,
    color: "text-teal-700",
    bg: "bg-teal-50",
    border: "border-teal-200",
    iconColor: "text-teal-600",
  },
  decision: {
    icon: ListChecks,
    color: "text-orange-700",
    bg: "bg-orange-50",
    border: "border-orange-200",
    iconColor: "text-orange-600",
  },
  createRequest: {
    icon: SendHorizonal,
    color: "text-cyan-700",
    bg: "bg-cyan-50",
    border: "border-cyan-200",
    iconColor: "text-cyan-600",
  },
  automation: {
    icon: Bot,
    color: "text-fuchsia-700",
    bg: "bg-fuchsia-50",
    border: "border-fuchsia-200",
    iconColor: "text-fuchsia-600",
  },
  documentCollection: {
    icon: FileStack,
    color: "text-purple-700",
    bg: "bg-purple-50",
    border: "border-purple-200",
    iconColor: "text-purple-600",
  },
};

function ProductBuilderNodeBase({
  data,
  selected,
}: NodeProps<BuilderNode>): JSX.Element {
  const { t } = useTranslation();
  const style = NODE_STYLES[data.__nodeType];
  const Icon = style.icon;

  if (data.__nodeType === "branch") {
    return <BranchNode data={data} selected={selected} />;
  }

  if (data.__nodeType === "decision") {
    return <DecisionNode data={data} selected={selected} />;
  }

  const displayLabel =
    data.label?.trim() || t(`enterpriseProductBuilder.nodeType.${data.__nodeType}`);

  return (
    <div
      className={cn(
        "min-w-[180px] max-w-[240px] rounded-lg border-2 px-3 py-2.5 shadow-sm transition-all",
        style.bg,
        style.border,
        selected && "ring-2 ring-offset-2 ring-primary",
        data.__hasError && "ring-2 ring-destructive ring-offset-1",
      )}
    >
      {data.__nodeType !== "start" && (
        <Handle
          type="target"
          position={Position.Top}
          className="!h-2.5 !w-2.5 !border-2 !border-white !bg-slate-400"
        />
      )}

      <div className="flex items-start gap-2">
        <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", style.iconColor)} />
        <div className="min-w-0 flex-1">
          <div className={cn("truncate text-sm font-semibold", style.color)}>
            {displayLabel}
          </div>
          {data.description && (
            <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
              {data.description}
            </div>
          )}
        </div>
      </div>

      {data.__nodeType === "precondition" ? (
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            id="pass"
            style={{ left: "30%" }}
            className="!h-2.5 !w-2.5 !border-2 !border-white !bg-emerald-500"
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="fail"
            style={{ left: "70%" }}
            className="!h-2.5 !w-2.5 !border-2 !border-white !bg-rose-500"
          />
        </>
      ) : data.__nodeType !== "end" ? (
        <Handle
          type="source"
          position={Position.Bottom}
          className="!h-2.5 !w-2.5 !border-2 !border-white !bg-slate-400"
        />
      ) : null}
    </div>
  );
}

function BranchNode({
  data,
  selected,
}: {
  data: BuilderNodeData;
  selected: boolean | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  const style = NODE_STYLES.branch;
  const Icon = style.icon;
  const cases = data.__branchCases ?? [];
  const rows: BuilderNodeCase[] = [
    ...cases,
    { id: "default", label: "default" },
  ];

  const displayLabel =
    data.label?.trim() || t("enterpriseProductBuilder.nodeType.branch");

  return (
    <div
      className={cn(
        "relative w-[240px] rounded-lg border-2 shadow-sm transition-all",
        style.bg,
        style.border,
        selected && "ring-2 ring-offset-2 ring-primary",
        data.__hasError && "ring-2 ring-destructive ring-offset-1",
      )}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!h-2.5 !w-2.5 !border-2 !border-white !bg-slate-400"
      />

      <div className="flex items-start gap-2 border-b border-rose-200/60 px-3 py-2">
        <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", style.iconColor)} />
        <div className="min-w-0 flex-1">
          <div className={cn("truncate text-sm font-semibold", style.color)}>
            {displayLabel}
          </div>
          {data.description && (
            <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
              {data.description}
            </div>
          )}
        </div>
      </div>

      <div className="divide-y divide-rose-200/40">
        {rows.map((row) => {
          const isDefault = row.id === "default";
          return (
            <div
              key={row.id}
              className={cn(
                "relative flex items-center justify-between px-3 py-1.5 text-xs",
                isDefault
                  ? "italic text-muted-foreground"
                  : "text-rose-800",
              )}
            >
              <span className="truncate">{row.label}</span>
              <Handle
                type="source"
                position={Position.Right}
                id={row.id}
                className={cn(
                  "!h-2.5 !w-2.5 !border-2 !border-white",
                  isDefault ? "!bg-slate-400" : "!bg-rose-500",
                )}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DecisionNode({
  data,
  selected,
}: {
  data: BuilderNodeData;
  selected: boolean | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  const style = NODE_STYLES.decision;
  const Icon = style.icon;
  const options = data.__decisionOptions ?? [];
  const rows: BuilderNodeCase[] = [
    ...options,
    { id: "default", label: "default" },
  ];

  const displayLabel =
    data.label?.trim() || t("enterpriseProductBuilder.nodeType.decision");

  return (
    <div
      className={cn(
        "relative w-[240px] rounded-lg border-2 shadow-sm transition-all",
        style.bg,
        style.border,
        selected && "ring-2 ring-offset-2 ring-primary",
        data.__hasError && "ring-2 ring-destructive ring-offset-1",
      )}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!h-2.5 !w-2.5 !border-2 !border-white !bg-slate-400"
      />

      <div className="flex items-start gap-2 border-b border-orange-200/60 px-3 py-2">
        <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", style.iconColor)} />
        <div className="min-w-0 flex-1">
          <div className={cn("truncate text-sm font-semibold", style.color)}>
            {displayLabel}
          </div>
          {data.description && (
            <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
              {data.description}
            </div>
          )}
        </div>
      </div>

      <div className="divide-y divide-orange-200/40">
        {rows.map((row) => {
          const isDefault = row.id === "default";
          return (
            <div
              key={row.id}
              className={cn(
                "relative flex items-center justify-between px-3 py-1.5 text-xs",
                isDefault
                  ? "italic text-muted-foreground"
                  : "text-orange-800",
              )}
            >
              <span className="truncate">{row.label}</span>
              <Handle
                type="source"
                position={Position.Right}
                id={row.id}
                className={cn(
                  "!h-2.5 !w-2.5 !border-2 !border-white",
                  isDefault ? "!bg-slate-400" : "!bg-orange-500",
                )}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const ProductBuilderNode = memo(ProductBuilderNodeBase);
